import { Dialog } from '@headlessui/react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';

import { Modal } from '@/components/common/modal';
import { Button, ButtonVariant } from '@/components/form/button';

import { unlinkBatchFromFermentation } from '../../services/batches';

interface IProps {
  /** based on the state, modal will be rendered */
  isOpen: boolean;

  /** toggle modal open and close */
  toggle: () => void;

  /** The ID of the batch to unlink from its parent fermentation */
  batchId: string;

  /** The name of the measurement to unlink */
  name: string;

  onSuccess?: () => void;
}

export const UnlinkFermentationModal: React.FC<IProps> = ({
  batchId,
  name,
  onSuccess,
  isOpen,
  toggle,
}) => {
  const { t } = useTranslation('translation', { useSuspense: false });

  const unlinkMutation = useMutation({
    mutationFn: ({ batchId }: { batchId: string }) =>
      unlinkBatchFromFermentation(batchId),
    onSuccess: () => {
      toast.success(t('fermentations.unlink.successMessage'));
      onSuccess && onSuccess();
    },
    onError: async () => {
      toast.error(t('fermentations.unlink.errorMessage'));
    },
    onSettled: () => {
      toggle();
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      title={t('fermentations.unlink.title')}
      conatinerClassnames="w-full md:w-5/6 lg:w-2/3 xl:w-1/2 2xl:w-1/3"
    >
      <div className="flex flex-col gap-3 py-6">
        <Dialog.Description>
          <Trans
            i18nKey="fermentations.unlink.description"
            values={{ name }}
            components={{ strong: <strong /> }}
          />
        </Dialog.Description>
      </div>
      <div className="flex justify-end gap-4">
        <Button
          variant={ButtonVariant.Secondary}
          onClick={() => toggle()}
          disabled={unlinkMutation.isLoading}
        >
          {t('common.no')}
        </Button>
        <Button
          onClick={() => unlinkMutation.mutate({ batchId })}
          isLoading={unlinkMutation.isLoading}
        >
          {t('common.yes')}
        </Button>
      </div>
    </Modal>
  );
};
